import { Link } from "react-router-dom";
import {
  LogOut,
  Plus,
  UserRound,
  Copy,
  Check,
  Server,
  ArrowRight,
  Crown,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { useApi } from "@/hooks/useApi";
import { getCurrentUser, redirectToLogin, logout, refreshSession } from "@/api/auth";
import { consumeSessionExpired } from "@/api/client";
import { listGuilds } from "@/api/guilds";
import { guildIconUrl, inviteForGuild } from "@/api/discordAssets";
import { DISCORD_INVITE_URL, LOGO_URL } from "@/config";
import { PageHeader } from "@/components/PageHeader";
import { GlassCard } from "@/components/GlassCard";
import { Reveal } from "@/components/Reveal";
import { OfflineState, ErrorState, EmptyState } from "@/components/States";

const avatarUrl = (user) => {
  if (!user) return LOGO_URL;
  if (typeof user.avatar === "string" && user.avatar.startsWith("http")) return user.avatar;
  if (user.avatar && user.id) return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=256`;
  return LOGO_URL;
};

export const SignInPrompt = ({ testId = "dashboard-signin-prompt" }) => {
  const [expired] = useState(() => consumeSessionExpired());
  return (
  <GlassCard className="mx-auto flex max-w-xl flex-col items-center p-10 text-center md:p-14" hover={false} data-testid={testId}>
    <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
      <UserRound className="h-6 w-6 text-paper-100" aria-hidden="true" />
    </span>
    {expired && (
      <p className="mt-6 rounded-full border border-white/20 bg-white/[0.05] px-4 py-1.5 text-xs font-semibold text-paper-100" data-testid="session-expired-notice">
        Your session expired — sign in again to continue.
      </p>
    )}
    <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-paper-100">
      Sign in to view your dashboard
    </h2>
    <p className="mt-3 max-w-sm text-sm leading-relaxed text-paper-400">
      Manage your servers, configure Xevon modules, and review activity — sign in with the Discord
      account you use with Xevon.
    </p>
    <button
      onClick={redirectToLogin}
      data-testid="dashboard-login-button"
      className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
    >
      Login with Discord
    </button>
  </GlassCard>
  );
};

const GuildIcon = ({ guild, className = "h-14 w-14" }) => {
  const url = guildIconUrl(guild);
  if (url) {
    return (
      <img
        src={url}
        alt={`${guild.name} icon`}
        width={56}
        height={56}
        loading="lazy"
        decoding="async"
        className={`${className} rounded-2xl border border-white/10 object-cover`}
      />
    );
  }
  return (
    <span className={`${className} flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] font-display text-xl font-semibold text-paper-200`} aria-hidden="true">
      {(guild.name || "?").slice(0, 1).toUpperCase()}
    </span>
  );
};

const ServerCard = ({ guild, index }) => (
  <Reveal delay={(index % 3) * 0.05}>
    <GlassCard className="h-full p-4 sm:p-6" data-testid={`server-card-${guild.id}`}>
      {/* Mobile: compact row */}
      <div className="flex items-center gap-4 sm:hidden">
        <GuildIcon guild={guild} className="h-12 w-12" />
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 font-display text-base font-semibold text-paper-100">
            <span className="truncate">{guild.name}</span>
            {guild.owner && <Crown className="h-3.5 w-3.5 shrink-0 text-paper-400" aria-label="You own this server" />}
          </h3>
          <p className="mt-0.5 truncate font-mono text-[11px] text-paper-500">
            {typeof guild.member_count === "number" ? `${guild.member_count.toLocaleString("en-US")} members` : `ID ${guild.id}`}
            {" · "}
            <span className={guild.xevon_installed ? "text-paper-200" : "text-paper-500"} data-testid={`server-status-${guild.id}`}>
              {guild.xevon_installed ? "Installed" : "Not installed"}
            </span>
          </p>
        </div>
        {guild.xevon_installed ? (
          <Link
            to={`/dashboard/servers/${guild.id}`}
            data-testid={`server-manage-${guild.id}`}
            aria-label={`Manage ${guild.name}`}
            className="shrink-0 rounded-full bg-white p-3 text-black transition-transform duration-300 hover:scale-[1.05]"
          >
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : (
          <a
            href={inviteForGuild(DISCORD_INVITE_URL, guild.id)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`server-invite-${guild.id}`}
            aria-label={`Add Xevon to ${guild.name}`}
            className="shrink-0 rounded-full border border-white/20 p-3 text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
      </div>

      {/* Desktop: full card */}
      <div className="hidden h-full flex-col sm:flex">
        <div className="flex items-start justify-between gap-3">
          <GuildIcon guild={guild} />
          <span
            className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
              guild.xevon_installed ? "border-white/25 text-paper-100" : "border-white/10 text-paper-500"
            }`}
          >
            {guild.xevon_installed ? "Installed" : "Not installed"}
          </span>
        </div>
        <h3 className="mt-4 flex items-center gap-2 truncate font-display text-lg font-semibold text-paper-100">
          <span className="truncate">{guild.name}</span>
          {guild.owner && <Crown className="h-4 w-4 shrink-0 text-paper-400" aria-label="You own this server" />}
        </h3>
        <p className="mt-1 flex-1 font-mono text-xs text-paper-500">
          {typeof guild.member_count === "number" ? `${guild.member_count.toLocaleString("en-US")} members` : `ID ${guild.id}`}
        </p>
        {guild.xevon_installed ? (
          <Link
            to={`/dashboard/servers/${guild.id}`}
            data-testid={`server-manage-desktop-${guild.id}`}
            className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.02]"
          >
            Manage
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        ) : (
          <a
            href={inviteForGuild(DISCORD_INVITE_URL, guild.id)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`server-invite-desktop-${guild.id}`}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add Xevon
          </a>
        )}
      </div>
    </GlassCard>
  </Reveal>
);

const ServersSection = () => {
  const { data, loading, error, refetch } = useApi(listGuilds);
  const [refreshing, setRefreshing] = useState(false);

  const resync = async () => {
    setRefreshing(true);
    try {
      await refreshSession();
      await refetch();
      toast.success("Server list refreshed from Discord.");
    } catch {
      toast.error("Could not refresh your server list right now.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="servers-loading" role="status" aria-label="Loading servers">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-56 animate-pulse rounded-2xl border border-white/5 bg-white/[0.03]" />
        ))}
      </div>
    );
  }

  if (error) {
    if (error.kind === "offline" || error.kind === "timeout") return <OfflineState onRetry={refetch} />;
    if (error.kind === "auth") return null;
    if (error.kind === "http" && error.status === 404) {
      return (
        <EmptyState
          title="Server management isn't live on the API yet."
          message="The dashboard API endpoints (v2) haven't been deployed to the Xevon API. Once they are, your servers will appear here automatically."
        />
      );
    }
    return <ErrorState onRetry={refetch} />;
  }

  const guilds = data?.guilds || [];

  return (
    <div data-testid="servers-section">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h2 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl">
          <Server className="h-6 w-6" aria-hidden="true" />
          Your Servers
        </h2>
        <button
          onClick={resync}
          disabled={refreshing}
          data-testid="servers-refresh-button"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-sm font-semibold text-paper-200 transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          {refreshing ? "Refreshing…" : "Re-sync from Discord"}
        </button>
      </div>
      {guilds.length === 0 ? (
        <EmptyState
          title="No manageable servers found."
          message="You need the Administrator or Manage Server permission in a server for it to appear here."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {guilds.map((guild, i) => (
            <ServerCard key={guild.id} guild={guild} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  useSEO({ title: "Dashboard", description: "Your Xevon account dashboard.", path: "/dashboard", noindex: true });

  const { data: user, loading, error, refetch } = useApi(getCurrentUser);
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    if (!user?.id) return;
    try {
      await navigator.clipboard.writeText(String(user.id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  const displayName = user?.global_name || user?.username || "Discord User";

  return (
    <>
      <PageHeader
        kicker="Dashboard"
        title={user ? `Welcome back, ${displayName}.` : "Your Xevon account."}
        description="Your servers, your configuration, your activity — everything in one place."
      />
      <section className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="dashboard-page">
        {loading && (
          <div className="grid gap-4 lg:grid-cols-[1fr_2fr]" data-testid="dashboard-loading" role="status" aria-label="Loading dashboard">
            <div className="h-52 animate-pulse rounded-2xl border border-white/5 bg-white/[0.03]" />
            <div className="h-52 animate-pulse rounded-2xl border border-white/5 bg-white/[0.03]" />
          </div>
        )}

        {!loading && error && error.kind === "auth" && <SignInPrompt />}
        {!loading && error && (error.kind === "offline" || error.kind === "timeout") && <OfflineState onRetry={refetch} />}
        {!loading && error && !["auth", "offline", "timeout"].includes(error.kind) && <ErrorState onRetry={refetch} />}

        {!loading && user && (
          <>
            <Reveal>
              <GlassCard className="mb-14 p-6 md:p-8" hover={false} data-testid="dashboard-profile-card">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-5">
                    <img
                      src={avatarUrl(user)}
                      alt={`${displayName}'s Discord avatar`}
                      width={64}
                      height={64}
                      decoding="async"
                      className="h-16 w-16 rounded-full border border-white/20 object-cover"
                      data-testid="dashboard-avatar"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Signed in as</p>
                      <h2 className="mt-1 truncate font-display text-xl font-semibold tracking-tight text-paper-100" data-testid="dashboard-username">
                        {displayName}
                      </h2>
                      {user.id && (
                        <button
                          onClick={copyId}
                          data-testid="dashboard-copy-id"
                          className="group mt-1.5 inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 font-mono text-xs text-paper-400 transition-colors duration-300 hover:border-white/30 hover:text-paper-100"
                          aria-label="Copy Discord ID"
                        >
                          ID {user.id}
                          {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5 opacity-60 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />}
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => logout().finally(() => window.location.assign("/"))}
                    data-testid="dashboard-logout-button"
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    Sign out
                  </button>
                </div>
              </GlassCard>
            </Reveal>
            <ServersSection />
          </>
        )}
      </section>
    </>
  );
}
